import { useState } from 'react';
import { useStore } from '../../../../../store/mst';
import { GraphQLErrorCodes } from '../../../../../constants';
import getAppEnv from '../../../../../store/mst/utils/getAppEnv';
import * as DuplicateRouteNotifications from '../../../../../notifications/routes/duplicateRoute';

interface UseDuplicateRouteReturn {
  isSubmitting: boolean;
  duplicateRoutes: (routeUuids: string[], targetDate: string) => Promise<void>;
  error: string | null;
}

const useDuplicateRoute = (): UseDuplicateRouteReturn => {
  const store = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedWorkspaceId = store.ui.selectedWorkspace?.id;

  const duplicateRoutes = async (routeUuids: string[], targetDate: string): Promise<void> => {
    if (!selectedWorkspaceId || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    store.ui.setIsDuplicatingRoute({
      targetDate,
      routeCount: routeUuids.length,
    });

    if (store.ui.selectedRoutesBulk.size > 0) {
      store.ui.clearSelectedRoutesBulk();
    }

    try {
      await store.model.mutateDuplicateRoute({
        projectId: selectedWorkspaceId,
        routeUuids,
        targetDate,
      }, (b) => b.actionUuid.actionName).currentPromise();

      setIsSubmitting(false);
    } catch (err: any) {
      console.error('Failed to duplicate route:', err);
      setIsSubmitting(false);

      const responseErrors = err?.response?.errors;
      const hasDuplicateRouteError = responseErrors?.some(
        (gqlError: any) => gqlError.extensions?.code === GraphQLErrorCodes.DUPLICATE_ROUTE_FAILED,
      );

      // Only show notification if it's not a duplicate route error and workspace exists
      if (!hasDuplicateRouteError && selectedWorkspaceId) {
        const appEnv = getAppEnv(store.model);
        const errorMessage = responseErrors?.[0]?.message || err?.message || 'Failed to duplicate route';
        const count = routeUuids.length;

        appEnv.notification(
          DuplicateRouteNotifications.duplicateRouteFailureNotification(
            selectedWorkspaceId,
            errorMessage,
            count,
          ),
        );

        setError(errorMessage);
      }
    }
  };

  return {
    isSubmitting,
    duplicateRoutes,
    error,
  };
};

export default useDuplicateRoute;
